import { centroidOfRows, rankRowsBySimilarity } from './embeddingMath'
import { CellEmbeddings } from './useCellEmbeddings'

export const DEFAULT_TOP_K = 300

export interface FindSimilarOptions {
  /** Include already-labeled cells among the candidates (default false) */
  includeLabeled?: boolean
  /** Max number of results (default DEFAULT_TOP_K; <= 0 returns all) */
  topK?: number
}

/** Candidate rows to rank, excluding a set and optionally all labeled cells. */
const buildCandidateRows = (
  embeddings: CellEmbeddings,
  labels: Record<string, string>,
  includeLabeled: boolean,
  excludeLabel?: string
): number[] => {
  const rows: number[] = []
  embeddings.cellIdByRow.forEach((cellId, row) => {
    const cellLabel = labels[cellId]
    const isExcludedLabel = excludeLabel !== undefined && cellLabel === excludeLabel
    const isLabeled = Boolean(cellLabel)
    const skip = isExcludedLabel || (!includeLabeled && isLabeled)
    if (!skip) rows.push(row)
  })
  return rows
}

/**
 * Ranks cells by similarity to the centroid of all cells already labeled
 * `label`. Use this to surface more cells that look like the ones you have
 * confirmed (e.g. "find more fnRBC"). Returns ordered cellIds, closest first.
 */
export const findSimilarToLabel = (
  embeddings: CellEmbeddings,
  labels: Record<string, string>,
  label: string,
  options: FindSimilarOptions = {}
): string[] => {
  if (!embeddings.available || !label) return []
  const { includeLabeled = false, topK = DEFAULT_TOP_K } = options

  const targetRows: number[] = []
  Object.keys(labels).forEach((cellId) => {
    if (labels[cellId] === label) {
      const row = embeddings.rowByCellId.get(cellId)
      if (row !== undefined) targetRows.push(row)
    }
  })
  if (targetRows.length === 0) return []

  const centroid = centroidOfRows(embeddings.matrix, embeddings.dim, targetRows)
  const candidates = buildCandidateRows(embeddings, labels, includeLabeled, label)
  const ranked = rankRowsBySimilarity(embeddings.matrix, embeddings.dim, centroid, candidates, topK)
  return ranked.map(({ row }) => embeddings.cellIdByRow[row])
}

/**
 * Ranks cells by similarity to a single reference cell. Returns ordered
 * cellIds, closest first (excluding the reference cell itself).
 */
export const findSimilarToCell = (
  embeddings: CellEmbeddings,
  cellId: string,
  labels: Record<string, string>,
  options: FindSimilarOptions = {}
): string[] => {
  if (!embeddings.available) return []
  const { includeLabeled = false, topK = DEFAULT_TOP_K } = options

  const queryRow = embeddings.rowByCellId.get(cellId)
  if (queryRow === undefined) return []

  const base = queryRow * embeddings.dim
  const query = embeddings.matrix.subarray(base, base + embeddings.dim)
  const candidates = buildCandidateRows(embeddings, labels, includeLabeled).filter(
    (row) => row !== queryRow
  )
  const ranked = rankRowsBySimilarity(embeddings.matrix, embeddings.dim, query, candidates, topK)
  return ranked.map(({ row }) => embeddings.cellIdByRow[row])
}
